// src/components/admin/questions/CategoriesTab.tsx

import React, { useState, useEffect } from 'react';
import { Plus, FolderTree, Pencil, Check, X, Trash2, ChevronRight, ChevronDown, AlertTriangle } from 'lucide-react';
import { questionService } from '../../../services/questionService';

interface CategoriesTabProps {
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

// ── Modal de confirmação de exclusão ─────────────────────────────
interface DeleteModalProps {
    category: any;
    onConfirm: () => void;
    onCancel: () => void;
    loading: boolean;
    questionCount: number | null;
}

const DeleteModal: React.FC<DeleteModalProps> = ({ category, onConfirm, onCancel, loading, questionCount }) => (
    <div className="cat-modal-overlay" onClick={onCancel}>
        <div className="cat-modal" onClick={e => e.stopPropagation()}>
            <div className="cat-modal-icon">
                <AlertTriangle size={28} />
            </div>
            <h3 className="cat-modal-title">Excluir categoria</h3>
            <p className="cat-modal-desc">
                Tem certeza que deseja excluir <strong>{category.name}</strong>
                {category.level > 1 ? ` e todas as suas subcategorias` : ''}?
            </p>
            {questionCount !== null && questionCount > 0 && (
                <div className="cat-modal-warning">
                    <AlertTriangle size={14} />
                    <span>
                        <strong>{questionCount}</strong> questão(ões) ficarão sem categoria e precisarão
                        ser recategorizadas manualmente.
                    </span>
                </div>
            )}
            {questionCount === 0 && (
                <p className="cat-modal-safe">Nenhuma questão será afetada.</p>
            )}
            <div className="cat-modal-actions">
                <button className="cat-modal-btn cat-modal-btn--cancel" onClick={onCancel} disabled={loading}>
                    Cancelar
                </button>
                <button className="cat-modal-btn cat-modal-btn--delete" onClick={onConfirm} disabled={loading}>
                    {loading ? 'Excluindo...' : 'Sim, excluir'}
                </button>
            </div>
        </div>
    </div>
);

const CategoriesTab: React.FC<CategoriesTabProps> = ({ onShowToast }) => {
    const [categoryTree, setCategoryTree] = useState<any[]>([]);
    const [flatCategories, setFlatCategories] = useState<any[]>([]);
    const [categoryName, setCategoryName] = useState('');
    const [categoryLevel, setCategoryLevel] = useState(1);
    const [parentCategoryId, setParentCategoryId] = useState<string | null>(null);
    const [creating, setCreating] = useState(false);

    // Modal state
    const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteQuestionCount, setDeleteQuestionCount] = useState<number | null>(null);

    useEffect(() => { loadCategories(); }, []);

    const loadCategories = async () => {
        try {
            const tree = await questionService.getCategoriesTree();
            setCategoryTree(tree);

            // Flatten para os selects de pai
            const flat: any[] = [];
            const flatten = (nodes: any[], parentName: string | null = null) => {
                nodes.forEach(node => {
                    flat.push({ _id: node._id, name: node.name, level: node.level, parentName, questionsCount: node.questionsCount });
                    if (node.children?.length > 0) flatten(node.children, node.name);
                });
            };
            flatten(tree);
            setFlatCategories(flat);
        } catch { /* silencioso */ }
    };

    const handleCreateCategory = async () => {
        if (!categoryName.trim()) { onShowToast('Nome da categoria é obrigatório', 'error'); return; }
        setCreating(true);
        try {
            await questionService.createCategory({ name: categoryName.trim(), level: categoryLevel, parentId: parentCategoryId });
            onShowToast('Categoria criada com sucesso', 'success');
            setCategoryName('');
            setCategoryLevel(1);
            setParentCategoryId(null);
            loadCategories();
        } catch {
            onShowToast('Erro ao criar categoria', 'error');
        } finally {
            setCreating(false);
        }
    };

    const handleDeleteRequest = async (cat: any) => {
        setDeleteTarget(cat);
        setDeleteQuestionCount(null);
        try {
            const count = await questionService.getCategoryQuestionCount(cat._id);
            setDeleteQuestionCount(count);
        } catch { setDeleteQuestionCount(0); }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        try {
            const result = await questionService.deleteCategory(deleteTarget._id);
            onShowToast(result.message || 'Categoria excluída', 'success');
            setDeleteTarget(null);
            loadCategories();
        } catch (e: any) {
            onShowToast(e.message || 'Erro ao excluir categoria', 'error');
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <div className="qm-block cat-vertical-layout">

            {/* ── Form de criação — largura total ── */}
            <div className="cat-create-section">
                <h3 className="cat-section-title">Nova Categoria</h3>
                <div className="cat-create-form">
                    <div className="cat-form-row">
                        <input
                            type="text"
                            className="cat-form-input"
                            value={categoryName}
                            onChange={e => setCategoryName(e.target.value)}
                            placeholder="Nome da categoria"
                            onKeyDown={e => { if (e.key === 'Enter') handleCreateCategory(); }}
                        />
                        <select
                            className="cat-form-select"
                            value={categoryLevel}
                            onChange={e => {
                                setCategoryLevel(Number(e.target.value));
                                if (Number(e.target.value) === 1) setParentCategoryId(null);
                            }}
                        >
                            <option value={1}>Nível 1 — Principal</option>
                            <option value={2}>Nível 2 — Subcategoria</option>
                            <option value={3}>Nível 3 — Sub-subcategoria</option>
                            <option value={4}>Nível 4 — Específico</option>
                        </select>
                        {categoryLevel > 1 && (
                            <select
                                className="cat-form-select"
                                value={parentCategoryId || ''}
                                onChange={e => setParentCategoryId(e.target.value || null)}
                            >
                                <option value="">Selecione categoria pai...</option>
                                {flatCategories
                                    .filter(c => c.level === categoryLevel - 1)
                                    .map(c => (
                                        <option key={c._id} value={c._id}>
                                            {'  '.repeat(c.level - 1)}└─ {c.name}
                                        </option>
                                    ))}
                            </select>
                        )}
                        <button
                            className="cat-form-btn"
                            onClick={handleCreateCategory}
                            disabled={creating || !categoryName.trim()}
                        >
                            <Plus size={15} />
                            {creating ? 'Criando...' : 'Criar Categoria'}
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Árvore de categorias ── */}
            <div className="cat-tree-section">
                <h3 className="cat-section-title">
                    Categorias Criadas
                    <span className="cat-section-count">({flatCategories.length})</span>
                </h3>
                <div className="cat-tree-container">
                    {categoryTree.length === 0 ? (
                        <p className="cat-empty-msg">Nenhuma categoria criada ainda.</p>
                    ) : (
                        <CategoryTreeView
                            nodes={categoryTree}
                            onShowToast={onShowToast}
                            onReload={loadCategories}
                            onDeleteRequest={handleDeleteRequest}
                        />
                    )}
                </div>
            </div>

            {/* ── Modal de confirmação ── */}
            {deleteTarget && (
                <DeleteModal
                    category={deleteTarget}
                    onConfirm={handleDeleteConfirm}
                    onCancel={() => setDeleteTarget(null)}
                    loading={deleteLoading}
                    questionCount={deleteQuestionCount}
                />
            )}
        </div>
    );
};

// ── Árvore recursiva ─────────────────────────────────────────────
interface CategoryTreeViewProps {
    nodes: any[];
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
    onReload: () => void;
    onDeleteRequest: (cat: any) => void;
}

const CategoryTreeView: React.FC<CategoryTreeViewProps> = ({ nodes, onShowToast, onReload, onDeleteRequest }) => {
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editingName, setEditingName] = useState('');
    const [savingId, setSavingId] = useState<string | null>(null);

    const toggle = (id: string) => {
        const s = new Set(expandedIds);
        s.has(id) ? s.delete(id) : s.add(id);
        setExpandedIds(s);
    };

    const startEdit = (cat: any) => {
        setEditingId(cat._id);
        setEditingName(cat.name);
    };

    const cancelEdit = () => { setEditingId(null); setEditingName(''); };

    const confirmEdit = async (id: string) => {
        if (!editingName.trim()) { onShowToast('Nome não pode ser vazio', 'error'); return; }
        try {
            setSavingId(id);
            await questionService.updateCategory(id, editingName.trim());
            onShowToast('Categoria atualizada', 'success');
            setEditingId(null);
            setEditingName('');
            onReload();
        } catch { onShowToast('Erro ao atualizar', 'error'); }
        finally { setSavingId(null); }
    };

    const renderNodes = (nodeList: any[], depth = 0): React.ReactNode => (
        <>
            {nodeList.map(cat => {
                const hasChildren = Array.isArray(cat.children) && cat.children.length > 0;
                const isExpanded = expandedIds.has(cat._id);
                const isEditing = editingId === cat._id;
                const isSaving = savingId === cat._id;

                return (
                    <div key={cat._id} className="cat-tree-node" style={{ paddingLeft: depth * 22 }}>
                        <div className="cat-node-content">
                            {/* Expand toggle */}
                            <button
                                className={`cat-node-toggle ${!hasChildren ? 'cat-node-toggle--leaf' : ''}`}
                                onClick={() => hasChildren && toggle(cat._id)}
                            >
                                {hasChildren
                                    ? (isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />)
                                    : <FolderTree size={13} />
                                }
                            </button>

                            {/* Nome editável */}
                            {isEditing ? (
                                <input
                                    className="cat-edit-input"
                                    value={editingName}
                                    onChange={e => setEditingName(e.target.value)}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter') confirmEdit(cat._id);
                                        if (e.key === 'Escape') cancelEdit();
                                    }}
                                    autoFocus
                                    disabled={isSaving}
                                />
                            ) : (
                                <span className="cat-node-name">{cat.name}</span>
                            )}

                            <span className="cat-node-level-tag">N{cat.level}</span>
                            <span className="cat-node-count">{cat.questionsCount ?? 0} q.</span>

                            {/* Ações */}
                            <div className="cat-node-actions">
                                {isEditing ? (
                                    <>
                                        <button className="cat-btn cat-btn--confirm" onClick={() => confirmEdit(cat._id)} disabled={isSaving} title="Confirmar">
                                            <Check size={13} />
                                        </button>
                                        <button className="cat-btn cat-btn--cancel" onClick={cancelEdit} disabled={isSaving} title="Cancelar">
                                            <X size={13} />
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button className="cat-btn cat-btn--edit" onClick={() => startEdit(cat)} title="Editar nome">
                                            <Pencil size={13} />
                                        </button>
                                        <button className="cat-btn cat-btn--delete" onClick={() => onDeleteRequest(cat)} title="Excluir categoria">
                                            <Trash2 size={13} />
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>

                        {hasChildren && isExpanded && (
                            <div className="cat-children">
                                {renderNodes(cat.children, depth + 1)}
                            </div>
                        )}
                    </div>
                );
            })}
        </>
    );

    return <>{renderNodes(nodes)}</>;
};

export default CategoriesTab;
